/**
 * Where the candidate is in a Digital SAT module: the open question (or the
 * check-your-work page), questions marked for review and crossed-out options.
 * Kept in this browser so a reload returns to the same place. One entry per
 * attempt, holding the open module only. Storage can be blocked (private mode,
 * full quota); the module then simply starts at its first question.
 */
export interface SatModuleState {
  index: number;
  reviewing: boolean;
  flagged: string[];
  /** Crossed-out option ids, by question id. */
  eliminated: Record<string, string[]>;
}

interface StoredSatModuleState extends SatModuleState {
  sectionId: string;
}

const storageKey = (attemptId: string) => `examing.sat-module.${attemptId}`;

const stringList = (value: unknown, allowed?: Set<string>): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && (!allowed || allowed.has(v))) : [];

/** The saved state of this module, trimmed to the questions it still has; null when there is none. */
export function loadSatModuleState(attemptId: string, sectionId: string, questionIds: string[]): SatModuleState | null {
  try {
    const raw = localStorage.getItem(storageKey(attemptId));
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredSatModuleState> | null;
    if (stored?.sectionId !== sectionId || !questionIds.length) return null;
    const ids = new Set(questionIds);
    const index = Number.isInteger(stored.index) ? Math.min(Math.max(stored.index as number, 0), questionIds.length - 1) : 0;
    const eliminated: Record<string, string[]> = {};
    if (stored.eliminated && typeof stored.eliminated === 'object') {
      for (const [questionId, options] of Object.entries(stored.eliminated)) {
        const list = stringList(options);
        if (ids.has(questionId) && list.length) eliminated[questionId] = list;
      }
    }
    return { index, reviewing: stored.reviewing === true, flagged: stringList(stored.flagged, ids), eliminated };
  } catch {
    return null;
  }
}

export function saveSatModuleState(attemptId: string, sectionId: string, state: SatModuleState): void {
  try {
    const stored: StoredSatModuleState = { sectionId, ...state };
    localStorage.setItem(storageKey(attemptId), JSON.stringify(stored));
  } catch {
    // Storage blocked: see above.
  }
}

export function clearSatModuleState(attemptId: string): void {
  try {
    localStorage.removeItem(storageKey(attemptId));
  } catch {
    // Storage blocked: see above.
  }
}
